import { RiArrowDownLine, RiArrowUpLine, RiLoader4Line } from "@remixicon/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { fetchClient } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

interface FulfillmentConfig {
	farfetchBranchPickStrategy?: string;
	farfetchBranchPriority?: number[] | null;
	farfetchBranchScope?: string;
	farfetchTransferBranch?: number | null;
}

interface BranchRow {
	number: number;
	name: string;
	isActive: boolean;
}

const CONFIG_KEY = ["get", "/api/v1/integrations/config"] as const;
const BRANCHES_KEY = ["prohandel", "branches"] as const;

export function FulfillmentSettings() {
	const qc = useQueryClient();
	const [strategy, setStrategy] = useState("highest_stock");
	const [scope, setScope] = useState("single_branch");
	const [priority, setPriority] = useState<number[]>([]);
	// "" = no transfer branch set, which disables the stock transfer entirely.
	const [transferBranch, setTransferBranch] = useState<string>("");
	const [saving, setSaving] = useState(false);

	const config = useQuery({
		queryKey: CONFIG_KEY,
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/api/v1/integrations/config" as never);
			if (error) throw new Error("failed to load config");
			return data as FulfillmentConfig;
		},
	});

	const branches = useQuery({
		queryKey: BRANCHES_KEY,
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/api/v1/prohandel/branches" as never);
			if (error) throw new Error("failed to load branches");
			return data as BranchRow[];
		},
	});

	useEffect(() => {
		if (!config.data) return;
		setStrategy(config.data.farfetchBranchPickStrategy ?? "highest_stock");
		setScope(config.data.farfetchBranchScope ?? "single_branch");
		setPriority(config.data.farfetchBranchPriority ?? []);
		setTransferBranch(
			config.data.farfetchTransferBranch != null ? String(config.data.farfetchTransferBranch) : "",
		);
	}, [config.data]);

	const activeBranches = (branches.data ?? []).filter((b) => b.isActive);

	// Branches not yet in the priority list (offered to append).
	const unlisted = activeBranches.filter((b) => !priority.includes(b.number));

	function move(index: number, delta: number) {
		setPriority((prev) => {
			const next = [...prev];
			const target = index + delta;
			const current = next[index];
			const swapWith = next[target];
			if (current === undefined || swapWith === undefined) return prev;
			next[index] = swapWith;
			next[target] = current;
			return next;
		});
	}

	function addToPriority(branchNumber: number) {
		setPriority((prev) => (prev.includes(branchNumber) ? prev : [...prev, branchNumber]));
	}

	function removeFromPriority(branchNumber: number) {
		setPriority((prev) => prev.filter((n) => n !== branchNumber));
	}

	async function handleSave() {
		setSaving(true);
		try {
			const body: Record<string, unknown> = {
				farfetchBranchPickStrategy: strategy,
				farfetchBranchScope: scope,
			};
			// Only send the priority list when the strategy uses it.
			if (strategy === "priority_order") {
				body.farfetchBranchPriority = priority;
			}
			// Only send when set: the backend treats absent as "unchanged", so an
			// empty selection must not be sent as 0 (which it rejects) nor silently
			// clear a previously configured branch.
			if (transferBranch !== "") {
				body.farfetchTransferBranch = Number(transferBranch);
			}
			const { error } = await fetchClient.PUT(
				"/api/v1/integrations/config" as never,
				{ body } as never,
			);
			if (error) {
				toast.error("Failed to save fulfillment settings");
				return;
			}
			toast.success("Fulfillment settings saved");
			qc.invalidateQueries({ queryKey: CONFIG_KEY });
		} finally {
			setSaving(false);
		}
	}

	if (config.isLoading) {
		return (
			<div data-testid="fulfillment-skeleton" className="space-y-2">
				<Skeleton className="h-9 w-full" />
				<Skeleton className="h-9 w-full" />
			</div>
		);
	}
	if (config.error) {
		return (
			<div
				role="alert"
				className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm space-y-2"
			>
				<p className="font-medium">Could not load fulfillment settings.</p>
				<Button type="button" variant="outline" size="sm" onClick={() => config.refetch()}>
					Retry
				</Button>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Pick strategy */}
			<div className="space-y-3">
				<Label className="font-medium">Branch pick strategy</Label>
				<RadioGroup value={strategy} onValueChange={setStrategy}>
					<div className="flex items-start gap-2">
						<RadioGroupItem value="highest_stock" id="strat-highest" />
						<Label htmlFor="strat-highest" className="font-normal">
							Highest stock - assign the active branch with the most stock of the item.
						</Label>
					</div>
					<div className="flex items-start gap-2">
						<RadioGroupItem value="priority_order" id="strat-priority" />
						<Label htmlFor="strat-priority" className="font-normal">
							Priority order - assign the first branch in your priority list that has stock.
						</Label>
					</div>
					<div className="flex items-start gap-2">
						<RadioGroupItem value="manual" id="strat-manual" />
						<Label htmlFor="strat-manual" className="font-normal">
							Manual - never auto-assign; each order waits for you to pick a branch.
						</Label>
					</div>
				</RadioGroup>
			</div>

			{/* Branch priority editor (only for priority_order) */}
			{strategy === "priority_order" && (
				<div className="space-y-2">
					<Label className="font-medium">Branch priority</Label>
					<p className="text-xs text-muted-foreground">
						Branches are tried top to bottom. The first one with stock for the item is chosen.
					</p>
					{priority.length === 0 ? (
						<p className="text-sm text-muted-foreground">No branches in the priority list yet.</p>
					) : (
						<ol className="space-y-1">
							{priority.map((branchNumber, index) => {
								const b = activeBranches.find((x) => x.number === branchNumber);
								return (
									<li
										key={branchNumber}
										className="flex items-center justify-between rounded-md border px-3 py-1.5 text-sm"
									>
										<span>
											{index + 1}. Branch {branchNumber}
											{b?.name ? ` - ${b.name}` : ""}
										</span>
										<span className="flex items-center gap-1">
											<Button
												type="button"
												variant="ghost"
												size="sm"
												aria-label={`Move branch ${branchNumber} up`}
												disabled={index === 0}
												onClick={() => move(index, -1)}
											>
												<RiArrowUpLine className="size-4" />
											</Button>
											<Button
												type="button"
												variant="ghost"
												size="sm"
												aria-label={`Move branch ${branchNumber} down`}
												disabled={index === priority.length - 1}
												onClick={() => move(index, 1)}
											>
												<RiArrowDownLine className="size-4" />
											</Button>
											<Button
												type="button"
												variant="ghost"
												size="sm"
												aria-label={`Remove branch ${branchNumber} from priority`}
												onClick={() => removeFromPriority(branchNumber)}
											>
												Remove
											</Button>
										</span>
									</li>
								);
							})}
						</ol>
					)}
					{unlisted.length > 0 && (
						<div className="flex flex-wrap gap-1 pt-1">
							{unlisted.map((b) => (
								<Button
									key={b.number}
									type="button"
									variant="outline"
									size="sm"
									onClick={() => addToPriority(b.number)}
								>
									+ {b.number}
									{b.name ? ` ${b.name}` : ""}
								</Button>
							))}
						</div>
					)}
				</div>
			)}

			{/* Scope */}
			<div className="space-y-3">
				<Label className="font-medium">Fulfillment scope</Label>
				<RadioGroup value={scope} onValueChange={setScope}>
					<div className="flex items-start gap-2">
						<RadioGroupItem value="single_branch" id="scope-single" />
						<Label htmlFor="scope-single" className="font-normal">
							Single branch - the whole order ships from one branch. If no single branch has stock
							for every line, the order is flagged for you.
						</Label>
					</div>
					<div className="flex items-start gap-2">
						<RadioGroupItem value="per_line" id="scope-per-line" />
						<Label htmlFor="scope-per-line" className="font-normal">
							Per line - each line is assigned its own branch (split fulfillment). Lines with no
							stock anywhere are flagged.
						</Label>
					</div>
				</RadioGroup>
			</div>

			<div className="space-y-2 border-t pt-4">
				<Label htmlFor="transfer-branch" className="font-medium">
					Farfetch transfer branch
				</Label>
				<p className="text-sm text-muted-foreground">
					When a store accepts a Farfetch order, the stock is transferred out of that store and into
					this branch, so it stops counting as available in the shop. Leave unset to disable
					transfers - orders are still assigned, but no stock moves.
				</p>
				<Select value={transferBranch} onValueChange={setTransferBranch}>
					<SelectTrigger id="transfer-branch" className="w-full sm:w-96">
						<SelectValue placeholder="No transfer branch (transfers disabled)" />
					</SelectTrigger>
					<SelectContent>
						{(branches.data ?? []).map((b) => (
							<SelectItem key={b.number} value={String(b.number)}>
								{b.number} - {b.name}
								{b.isActive ? "" : " (inactive)"}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<p className="text-xs text-muted-foreground">
					Inactive branches are listed too: the Farfetch transfer branch holds committed stock
					rather than sellable shop stock, so it is normally not an active selling location.
				</p>
			</div>

			<Button onClick={handleSave} disabled={saving}>
				{saving && <RiLoader4Line className="mr-1 size-4 animate-spin" />}
				Save
			</Button>
		</div>
	);
}
